/**
 * @module ViewControls
 */
import { Button, createStyles, Grid, makeStyles, Typography } from '@material-ui/core';
import { useRepository } from '@sensenet/hooks-react';
import React, { createElement, useEffect, useRef, useState } from 'react';
import MediaQuery from 'react-responsive';
import { isFullWidthField } from "../helpers.js";
import { reactControlMapper } from "../react-control-mapper.js";
const hasInputField = ['Name', 'FileName', 'ShortText', 'AutoComplete', 'Textarea', 'NumberField', 'RichTextEditor'];
/**
 * Interface for EditView properties
 */

const useStyles = makeStyles(() => {
  return createStyles({
    grid: {
      margin: '0 auto'
    },
    fieldWrapper: {},
    field: {},
    fieldFullWidth: {},
    actionButtonWrapper: {
      textAlign: 'right'
    },
    cancel: {
      marginRight: 20
    }
  });
});

/**
 * View Control for editing a Content, works with a single Content and based on the ReactControlMapper
 *
 * Usage:
 * ```html
 *  <EditView content={selectedContent} contentTypeName={selectedContent.Type} onSubmit={editSubmitClick} />
 * ```
 */
export const EditView = props => {
  var _props$localization, _props$localization2, _props$localization3, _props$localization4;

  const actionName = props.actionName || 'edit';
  const controlMapper = props.controlMapper || reactControlMapper(props.repository);
  const [schema, setSchema] = useState(controlMapper.getFullSchemaForContentType(props.contentTypeName, actionName));
  const contentRef = useRef({});
  const [content, setContent] = useState(contentRef.current);
  contentRef.current = content;
  const classes = useStyles(props);
  const repository = useRepository();
  const uniqueId = Date.now();
  let isAutofocusSet = false;

  const handleSubmit = event => {
    var _props$onSubmit;

    event.preventDefault();
    (_props$onSubmit = props.onSubmit) === null || _props$onSubmit === void 0 ? void 0 : _props$onSubmit.call(props, content, schema.schema.ContentTypeName);
  };

  const handleInputChange = (field, value) => {
    setContent({ ...contentRef.current,
      [field]: value
    });
  };

  useEffect(() => {
    const schemaObservable = repository.schemas.subscribeToSchemas(() => {
      setSchema(() => controlMapper.getFullSchemaForContentType(props.contentTypeName, actionName));
    });
    return () => schemaObservable.dispose();
  }, [repository.schemas, actionName, controlMapper, props.contentTypeName]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, props.showTitle && (props.renderTitle ? props.renderTitle() : /*#__PURE__*/React.createElement(Typography, {
    variant: "h5",
    gutterBottom: true
  }, `${actionName.charAt(0).toUpperCase()}${actionName.slice(1)} ${schema.schema.DisplayName}`)), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    component: 'form',
    id: `edit-form-${uniqueId}`,
    onSubmit: handleSubmit,
    spacing: 2,
    className: classes.grid
  }, schema.fieldMappings.sort((item1, item2) => (item2.fieldSettings.FieldIndex || 0) - (item1.fieldSettings.FieldIndex || 0)).map(field => {
    const autoFocus = hasInputField.includes(field.controlType.name) && !isAutofocusSet;
    const fieldControl = /*#__PURE__*/createElement(controlMapper.getControlForContentField(props.contentTypeName, field.fieldSettings.Name, actionName), {
      actionName,
      settings: field.fieldSettings,
      repository: props.repository,
      content: props.content,
      fieldValue: props.content ? props.content[field.fieldSettings.Name] : undefined,
      renderIcon: props.renderIcon,
      fieldOnChange: handleInputChange,
      extension: props.extension,
      hideDescription: props.hideDescription,
      uploadFolderPath: props.uploadFolderpath,
      autoFocus,
      localization: props.fieldLocalization,
      locale: props.locale
    });
    const isFullWidth = isFullWidthField(field, props.content || {
      Type: props.contentTypeName
    }, repository);

    if (autoFocus) {
      isAutofocusSet = true;
    }

    return /*#__PURE__*/React.createElement(Grid, {
      item: true,
      xs: 12,
      sm: 12,
      md: 12,
      lg: 12,
      xl: 12,
      key: field.fieldSettings.Name,
      className: classes.fieldWrapper
    }, /*#__PURE__*/React.createElement("div", {
      className: isFullWidth ? classes.fieldFullWidth : classes.field
    }, fieldControl));
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.actionButtonWrapper
  }, /*#__PURE__*/React.createElement(MediaQuery, {
    minDeviceWidth: 700
  }, /*#__PURE__*/React.createElement(Button, {
    "aria-label": ((_props$localization = props.localization) === null || _props$localization === void 0 ? void 0 : _props$localization.cancel) || 'Cancel',
    "data-test": "cancel",
    color: "default",
    className: classes.cancel,
    onClick: () => {
      var _props$handleCancel;

      return (_props$handleCancel = props.handleCancel) === null || _props$handleCancel === void 0 ? void 0 : _props$handleCancel.call(props);
    }
  }, ((_props$localization2 = props.localization) === null || _props$localization2 === void 0 ? void 0 : _props$localization2.cancel) || 'Cancel')), /*#__PURE__*/React.createElement(Button, {
    "aria-label": ((_props$localization3 = props.localization) === null || _props$localization3 === void 0 ? void 0 : _props$localization3.submit) || 'Submit',
    type: "submit",
    "data-test": "submit",
    form: `edit-form-${uniqueId}`,
    variant: "contained",
    color: "primary"
  }, ((_props$localization4 = props.localization) === null || _props$localization4 === void 0 ? void 0 : _props$localization4.submit) || 'Submit')));
};
//# sourceMappingURL=edit-view.js.map